<template>
  <!--
    Gift Box Reveal — automated, celebratory outcome animation for the
    Daily Giveaway pass.

    Pixel-perfect port of the Lovable source (amazon/GiftBoxReveal.tsx).
    Custom HTML/CSS + bespoke SVG animation (documented exception to the
    Vuetify-first rule per mobilepassvue-v4/CLAUDE.md — pixel-perfect port).

    Timeline (win):
      0.0s  overlay fades in, wrapped box drops from above with bounce
      0.9s  "Tap to open" pulse appears (or auto-advances after 5s idle)
     on tap → box shakes with growing glow (1.4s)
            → prize card rises from inside the box, confetti bursts, CTA fades in
  -->
  <Teleport to="body">
    <div class="gbr-overlay" role="dialog" aria-modal="true">
      <button
        id="giveaway-giftbox-close"
        type="button"
        aria-label="Close"
        class="gbr-close gtm-click"
        @click="safeClose"
      >
        <X :size="16" />
      </button>

      <!-- Radial glow behind the box, intensifies on shake/open -->
      <div class="gbr-glow" :style="glowStyle" />

      <div class="gbr-content">
        <!-- Header -->
        <div class="gbr-header">
          <div class="gbr-eyebrow">Check-in accepted</div>
          <div class="gbr-venue">{{ venueName }}</div>
        </div>

        <!-- Stage -->
        <div class="gbr-stage">
          <button
            v-if="phase !== 'opened'"
            id="giveaway-giftbox-box"
            type="button"
            aria-label="Open gift"
            class="gbr-box gtm-click"
            :style="boxStyle"
            @click="handleTap"
          >
            <!-- Soft under-shadow -->
            <div class="gbr-box-shadow" />

            <svg
              viewBox="0 0 200 200"
              class="gbr-box-svg"
              :style="isShaking ? { animation: 'gb-glow 220ms ease-in-out infinite' } : undefined"
            >
              <defs>
                <linearGradient id="gb-box" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" :stop-color="navyColor" />
                  <stop offset="100%" stop-color="#0f1620" />
                </linearGradient>
                <linearGradient id="gb-lid" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" :stop-color="navyColor" />
                  <stop offset="100%" stop-color="#1a2331" />
                </linearGradient>
                <linearGradient id="gb-ribbon" x1="0" y1="0" x2="1" y2="1">
                  <stop offset="0%" :stop-color="brandColor" />
                  <stop offset="50%" stop-color="#FFC26A" />
                  <stop offset="100%" :stop-color="brandColor" />
                </linearGradient>
              </defs>

              <!-- Box body -->
              <rect x="30" y="80" width="140" height="90" rx="6" fill="url(#gb-box)" />
              <!-- Front sheen -->
              <rect x="30" y="80" width="140" height="90" rx="6" fill="url(#gb-ribbon)" opacity="0.06" />
              <!-- Vertical ribbon on body -->
              <rect x="88" y="80" width="24" height="90" fill="url(#gb-ribbon)" />

              <!-- Lid -->
              <rect x="24" y="62" width="152" height="24" rx="4" fill="url(#gb-lid)" />
              <rect x="88" y="62" width="24" height="24" fill="url(#gb-ribbon)" />

              <!-- Bow -->
              <g transform="translate(100 58)">
                <ellipse cx="-14" cy="0" rx="16" ry="12" fill="url(#gb-ribbon)" />
                <ellipse cx="14" cy="0" rx="16" ry="12" fill="url(#gb-ribbon)" />
                <ellipse cx="-14" cy="0" rx="6" ry="5" :fill="navyColor" opacity="0.25" />
                <ellipse cx="14" cy="0" rx="6" ry="5" :fill="navyColor" opacity="0.25" />
                <circle cx="0" cy="0" r="7" fill="url(#gb-ribbon)" />
                <circle cx="0" cy="0" r="3" :fill="navyColor" opacity="0.35" />
              </g>

              <!-- Tiny sparkles that appear during shake -->
              <g v-if="isShaking" :fill="brandColor">
                <circle cx="40" cy="55" r="2.5">
                  <animate attributeName="opacity" values="0;1;0" dur="0.6s" repeatCount="indefinite" />
                </circle>
                <circle cx="165" cy="70" r="2">
                  <animate attributeName="opacity" values="0;1;0" dur="0.6s" begin="0.2s" repeatCount="indefinite" />
                </circle>
                <circle cx="170" cy="130" r="2.5">
                  <animate attributeName="opacity" values="0;1;0" dur="0.6s" begin="0.35s" repeatCount="indefinite" />
                </circle>
                <circle cx="30" cy="140" r="2">
                  <animate attributeName="opacity" values="0;1;0" dur="0.6s" begin="0.15s" repeatCount="indefinite" />
                </circle>
              </g>
            </svg>
          </button>

          <!-- Prize panel that rises from the opened box -->
          <div v-else class="gbr-prize">
            <!-- Burst rays behind prize on a win -->
            <div v-if="won" class="gbr-rays" :style="raysStyle" />

            <div class="gbr-prize-card" :style="prizeCardStyle">
              <template v-if="won">
                <div class="gbr-gift-badge">
                  <img v-if="prizeImg" :src="prizeImg" alt="" class="gbr-prize-img" />
                  <Gift v-else :size="36" :stroke-width="1.75" />
                </div>
                <div class="gbr-won-label">You won</div>
                <div class="gbr-prize-title">{{ prizeTitle }}</div>
                <div class="gbr-congrats">
                  <PartyPopper :size="14" />
                  Congratulations!
                </div>
              </template>
              <template v-else>
                <Sparkles :size="40" :stroke-width="1.5" class="gbr-lost-icon" />
                <div class="gbr-lost-title">Not this time</div>
                <div class="gbr-lost-msg">
                  {{ loseMessage }}
                </div>
              </template>
            </div>
          </div>
        </div>

        <!-- Caption / CTA -->
        <div class="gbr-caption">
          <div v-if="phase === 'idle'" class="gbr-caption-idle">
            <button
              id="giveaway-giftbox-tap-open"
              type="button"
              class="gbr-tap-btn gtm-click"
              style="animation: gb-pulse 1600ms ease-in-out infinite"
              @click="handleTap"
            >
              <Sparkles :size="14" />
              Tap to open
            </button>
          </div>

          <div v-else-if="phase === 'shaking'" class="gbr-caption-shaking">
            <div class="gbr-unwrapping">Unwrapping…</div>
          </div>

          <button
            v-else
            id="giveaway-giftbox-cta"
            type="button"
            class="gbr-cta gtm-click"
            :style="ctaStyle"
            @click="$emit('reveal-complete')"
          >
            {{ won ? 'See your prize' : 'Keep exploring' }}
          </button>
        </div>
      </div>

      <!-- Confetti fires the moment the box opens on a win -->
      <div v-if="phase === 'opened' && won" class="gbr-confetti">
        <span
          v-for="p in confettiPieces"
          :key="p.i"
          class="gbr-confetti-piece"
          :style="p.style"
        />
      </div>
    </div>
  </Teleport>
</template>

<script setup>
import { ref, computed, watch, onMounted, onBeforeUnmount } from 'vue'
// @lucide/vue is the sanctioned icon exception for pixel-perfect ports
// (1:1 parity with the lucide-react source) — see mobilepassvue-v4/CLAUDE.md.
import { Gift, PartyPopper, Sparkles, X } from '@lucide/vue'

const props = defineProps({
  loseMessage: { type: String, default: 'Your entry is in — try again on another featured day.' },
  outcome: { type: String, default: 'lost' }, // 'won' | 'lost'
  venueName: { type: String, default: '' },
  prizeTitle: { type: String, default: '' },
  prizeImg: { type: String, default: '' },
  brandColor: { type: String, default: '#FF9900' },
  navyColor: { type: String, default: '#232F3E' }
})

const emit = defineEmits(['reveal-complete', 'close'])

const AUTO_OPEN_MS = 5000

const phase = ref('idle') // 'idle' | 'shaking' | 'opened'
const won = computed(() => props.outcome === 'won')
const isShaking = computed(() => phase.value === 'shaking')

let autoTimer = null
let shakeTimer = null

// Mount-time guard: swallow any close click that fires in the first
// ~400ms after the reveal mounts. This defends against a stray tap that
// was in-flight when the check-in sheet closed and the reveal took its
// place at the same overlay coordinates (would otherwise land on the X).
let mountedAt = Date.now()

function safeClose() {
  if (Date.now() - mountedAt < 400) return
  emit('close')
}

function handleTap() {
  if (phase.value !== 'idle') return
  if (autoTimer) window.clearTimeout(autoTimer)
  phase.value = 'shaking'
}

// Shake → open sequence
watch(phase, (p) => {
  if (p !== 'shaking') return
  shakeTimer = window.setTimeout(() => {
    phase.value = 'opened'
  }, 1400)
})

onMounted(() => {
  mountedAt = Date.now()
  // Auto-advance so users who don't tap still get the reveal.
  autoTimer = window.setTimeout(() => {
    if (phase.value === 'idle') phase.value = 'shaking'
  }, AUTO_OPEN_MS)
})

onBeforeUnmount(() => {
  if (autoTimer) window.clearTimeout(autoTimer)
  if (shakeTimer) window.clearTimeout(shakeTimer)
})

const glowStyle = computed(() => ({
  background: `radial-gradient(circle at 50% 45%, ${won.value ? props.brandColor : '#5b6577'}55 0%, transparent 55%)`,
  opacity: phase.value === 'idle' ? 0.5 : 1
}))

const boxStyle = computed(() => ({
  animation: isShaking.value
    ? 'gb-shake 220ms ease-in-out infinite'
    : 'gb-drop 800ms cubic-bezier(0.34,1.56,0.64,1) both, gb-hover 2600ms ease-in-out 900ms infinite',
  color: props.brandColor
}))

const raysStyle = computed(() => {
  const c = props.brandColor
  return {
    background: `conic-gradient(from 0deg, ${c}00 0deg, ${c}55 20deg, ${c}00 40deg, ${c}00 60deg, ${c}55 80deg, ${c}00 100deg, ${c}00 120deg, ${c}55 140deg, ${c}00 160deg, ${c}00 180deg, ${c}55 200deg, ${c}00 220deg, ${c}00 240deg, ${c}55 260deg, ${c}00 280deg, ${c}00 300deg, ${c}55 320deg, ${c}00 340deg)`,
    maskImage: 'radial-gradient(circle at center, black 0%, black 40%, transparent 70%)',
    WebkitMaskImage: 'radial-gradient(circle at center, black 0%, black 40%, transparent 70%)',
    animation: 'gb-spin 12s linear infinite'
  }
})

const prizeCardStyle = computed(() => ({
  background: won.value
    ? `radial-gradient(circle at 50% 30%, ${props.brandColor} 0%, #E27C00 55%, ${props.navyColor} 110%)`
    : `linear-gradient(160deg, ${props.navyColor}, #3a4658)`
}))

const ctaStyle = computed(() => ({
  background: won.value ? props.brandColor : 'rgba(255,255,255,0.12)',
  color: won.value ? props.navyColor : '#fff',
  border: won.value ? 'none' : '1px solid rgba(255,255,255,0.25)'
}))

// 40 particles, stable pseudo-random distribution via index math (as source).
const confettiPieces = computed(() => {
  const palette = [props.brandColor, '#FFFFFF', '#FFD79A', props.navyColor, '#FFB84D']
  return Array.from({ length: 40 }, (_, i) => {
    const angle = (i / 40) * Math.PI * 2
    const dist = 140 + ((i * 37) % 120)
    const dx = Math.cos(angle) * dist
    const dy = Math.sin(angle) * dist - 30
    const rot = ((i * 53) % 720) - 360
    const c = palette[i % palette.length]
    const delay = (i % 8) * 25
    const dur = 900 + ((i * 41) % 500)
    return {
      i,
      style: {
        background: c,
        transform: 'translate(-50%,-50%)',
        animation: `gb-confetti ${dur}ms cubic-bezier(.2,.7,.3,1) ${delay}ms forwards`,
        '--dx': `${dx}px`,
        '--dy': `${dy}px`,
        '--rot': `${rot}deg`
      }
    }
  })
})
</script>

<style>
/* Keyframes are intentionally UNSCOPED: several are referenced from inline
   `style="animation: …"` bindings (as in the source), which scoped-style
   keyframe renaming would break. Names are gb-* prefixed to avoid collisions. */
@keyframes gb-drop {
  0%   { transform: translateY(-180%) rotate(-8deg); opacity: 0; }
  70%  { transform: translateY(8%) rotate(2deg); opacity: 1; }
  85%  { transform: translateY(-3%) rotate(-1deg); }
  100% { transform: translateY(0) rotate(0deg); opacity: 1; }
}
@keyframes gb-shake {
  0%, 100% { transform: translateX(0) rotate(0deg); }
  15% { transform: translateX(-6px) rotate(-3deg); }
  30% { transform: translateX(6px) rotate(3deg); }
  45% { transform: translateX(-7px) rotate(-4deg); }
  60% { transform: translateX(7px) rotate(4deg); }
  75% { transform: translateX(-4px) rotate(-2deg); }
  90% { transform: translateX(4px) rotate(2deg); }
}
@keyframes gb-pulse {
  0%, 100% { transform: scale(1); box-shadow: 0 0 0 0 rgba(255,255,255,0.35); }
  50% { transform: scale(1.04); box-shadow: 0 0 0 8px rgba(255,255,255,0); }
}
@keyframes gb-glow {
  0%, 100% { filter: drop-shadow(0 0 0px currentColor); }
  50% { filter: drop-shadow(0 0 16px currentColor); }
}
@keyframes gb-hover {
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(-4px); }
}
@keyframes gb-confetti {
  0%   { transform: translate(-50%,-50%) rotate(0deg); opacity: 1; }
  100% { transform: translate(calc(-50% + var(--dx)), calc(-50% + var(--dy))) rotate(var(--rot)); opacity: 0; }
}
@keyframes gb-spin {
  to { transform: rotate(360deg); }
}
/* tailwindcss-animate "animate-in" equivalents */
@keyframes gb-fade-in {
  from { opacity: 0; }
}
@keyframes gb-rise-in {
  from { opacity: 0; transform: translateY(8px); }
}
@keyframes gb-prize-in {
  from { opacity: 0; transform: translateY(16px) scale(0.9); }
}
</style>

<style scoped lang="scss">
/* absolute inset-0 z-[170] flex items-center justify-center overflow-hidden
   bg-black/75 backdrop-blur-sm animate-in fade-in duration-300
   (source portals into the phone frame; here: fixed full-viewport overlay) */
.gbr-overlay {
  position: fixed;
  inset: 0;
  z-index: 2400;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  /* Clamp everything (incl. the absolute-positioned X) to the pass column so
     the close button never floats to the viewport corner on wide screens. */
  margin: 0 auto;
  max-width: var(--project-max-width, 480px);
  background: rgba(0, 0, 0, 0.75);
  backdrop-filter: blur(4px);
  -webkit-backdrop-filter: blur(4px);
  animation: gb-fade-in 300ms ease;
  color: #fff;
}

/* absolute right-3 top-3 z-10 grid h-8 w-8 place-items-center rounded-full
   bg-white/15 text-white ring-1 ring-white/25 backdrop-blur */
.gbr-close {
  position: absolute;
  right: 12px;
  top: 12px;
  z-index: 10;
  display: grid;
  place-items: center;
  height: 32px;
  width: 32px;
  padding: 0;
  border: 0;
  border-radius: 9999px;
  background: rgba(255, 255, 255, 0.15);
  color: #fff;
  box-shadow: 0 0 0 1px rgba(255, 255, 255, 0.25);
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
  cursor: pointer;
}

/* pointer-events-none absolute inset-0 transition-opacity duration-700 */
.gbr-glow {
  pointer-events: none;
  position: absolute;
  inset: 0;
  transition: opacity 700ms;
}

/* relative flex w-[86%] max-w-sm flex-col items-center */
.gbr-content {
  position: relative;
  display: flex;
  width: 86%;
  max-width: 24rem;
  flex-direction: column;
  align-items: center;
}

/* text-center text-white/90 */
.gbr-header {
  text-align: center;
  color: rgba(255, 255, 255, 0.9);
}

/* font-display text-[10px] font-bold uppercase tracking-[0.28em] */
.gbr-eyebrow {
  font-family: var(--fr-font-display, inherit);
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.28em;
}

/* mt-1 font-serif-display text-lg font-black italic */
.gbr-venue {
  margin-top: 4px;
  font-family: var(--fr-font-serif-display, Georgia, 'Times New Roman', serif);
  font-size: 18px;
  line-height: 28px;
  font-weight: 900;
  font-style: italic;
}

/* relative mt-4 flex h-[280px] w-full items-center justify-center */
.gbr-stage {
  position: relative;
  margin-top: 16px;
  display: flex;
  height: 280px;
  width: 100%;
  align-items: center;
  justify-content: center;
}

/* relative h-56 w-56 outline-none (animation + color via inline style) */
.gbr-box {
  position: relative;
  height: 224px;
  width: 224px;
  padding: 0;
  border: 0;
  background: transparent;
  outline: none;
  cursor: pointer;
}

/* absolute bottom-3 left-1/2 h-3 w-40 -translate-x-1/2 rounded-[50%] bg-black/40 blur-md */
.gbr-box-shadow {
  position: absolute;
  bottom: 12px;
  left: 50%;
  height: 12px;
  width: 160px;
  transform: translateX(-50%);
  border-radius: 50%;
  background: rgba(0, 0, 0, 0.4);
  filter: blur(12px);
}

/* relative h-full w-full */
.gbr-box-svg {
  position: relative;
  height: 100%;
  width: 100%;
  display: block;
}

/* relative flex h-full w-full items-center justify-center */
.gbr-prize {
  position: relative;
  display: flex;
  height: 100%;
  width: 100%;
  align-items: center;
  justify-content: center;
}

/* pointer-events-none absolute inset-0 — inline `animation: spin` overrides the
   enter animation in the source too, so only the spin plays (faithful). */
.gbr-rays {
  pointer-events: none;
  position: absolute;
  inset: 0;
}

/* relative flex w-[92%] flex-col items-center rounded-2xl px-4 py-5 text-center
   text-white shadow-2xl animate-in fade-in zoom-in-90 slide-in-from-bottom-4 duration-500 */
.gbr-prize-card {
  position: relative;
  display: flex;
  width: 92%;
  flex-direction: column;
  align-items: center;
  border-radius: 16px;
  padding: 20px 16px;
  text-align: center;
  color: #fff;
  box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
  animation: gb-prize-in 500ms ease;
}

/* rounded-full bg-white/20 p-2.5 ring-2 ring-white/40 */
.gbr-gift-badge {
  border-radius: 9999px;
  background: rgba(255, 255, 255, 0.2);
  padding: 10px;
  box-shadow: 0 0 0 2px rgba(255, 255, 255, 0.4);
  display: grid;
  place-items: center;
}
.gbr-prize-img {
  width: 56px;
  height: 56px;
  border-radius: 9999px;
  object-fit: cover;
  display: block;
}

/* mt-2 font-display text-[10px] font-bold uppercase tracking-[0.28em] text-white/85 */
.gbr-won-label {
  margin-top: 8px;
  font-family: var(--fr-font-display, inherit);
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.28em;
  color: rgba(255, 255, 255, 0.85);
}

/* mt-0.5 font-serif-display text-2xl font-black italic leading-tight drop-shadow */
.gbr-prize-title {
  margin-top: 2px;
  font-family: var(--fr-font-serif-display, Georgia, 'Times New Roman', serif);
  font-size: 24px;
  font-weight: 900;
  font-style: italic;
  line-height: 1.25;
  filter: drop-shadow(0 1px 2px rgba(0, 0, 0, 0.1)) drop-shadow(0 1px 1px rgba(0, 0, 0, 0.06));
}

/* mt-1.5 inline-flex items-center gap-1 text-[11px] text-white/90 */
.gbr-congrats {
  margin-top: 6px;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 11px;
  color: rgba(255, 255, 255, 0.9);
}

/* h-10 w-10 text-white/60 */
.gbr-lost-icon {
  color: rgba(255, 255, 255, 0.6);
}

/* mt-2 font-serif-display text-xl font-black italic */
.gbr-lost-title {
  margin-top: 8px;
  font-family: var(--fr-font-serif-display, Georgia, 'Times New Roman', serif);
  font-size: 20px;
  line-height: 28px;
  font-weight: 900;
  font-style: italic;
}

/* mt-1 text-[11px] leading-snug text-white/70 */
.gbr-lost-msg {
  margin-top: 4px;
  font-size: 11px;
  line-height: 1.375;
  color: rgba(255, 255, 255, 0.7);
}

/* mt-3 h-14 w-full */
.gbr-caption {
  margin-top: 12px;
  height: 56px;
  width: 100%;
}

/* text-center animate-in fade-in duration-500 */
.gbr-caption-idle {
  text-align: center;
  animation: gb-fade-in 500ms ease;
}

/* inline-flex items-center gap-1.5 rounded-full bg-white/15 px-4 py-2 font-display
   text-[11px] font-bold uppercase tracking-[0.22em] text-white ring-1 ring-white/25
   backdrop-blur transition hover:bg-white/25 */
.gbr-tap-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  border: 0;
  border-radius: 9999px;
  background: rgba(255, 255, 255, 0.15);
  padding: 8px 16px;
  font-family: var(--fr-font-display, inherit);
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.22em;
  color: #fff;
  box-shadow: 0 0 0 1px rgba(255, 255, 255, 0.25);
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
  transition: background-color 150ms cubic-bezier(0.4, 0, 0.2, 1);
  cursor: pointer;

  &:hover {
    background: rgba(255, 255, 255, 0.25);
  }
}

/* mt-1.5 text-[10px] text-white/50 */
.gbr-auto-hint {
  margin-top: 6px;
  font-size: 10px;
  color: rgba(255, 255, 255, 0.5);
}

/* text-center text-white/80 animate-in fade-in duration-200 */
.gbr-caption-shaking {
  text-align: center;
  color: rgba(255, 255, 255, 0.8);
  animation: gb-fade-in 200ms ease;
}

/* font-display text-[11px] font-bold uppercase tracking-[0.28em] */
.gbr-unwrapping {
  font-family: var(--fr-font-display, inherit);
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.28em;
}

/* w-full rounded-xl px-3 py-3 font-display text-[12px] font-bold uppercase
   tracking-wider shadow-lg animate-in fade-in slide-in-from-bottom-2 duration-500 */
.gbr-cta {
  width: 100%;
  border-radius: 12px;
  padding: 12px;
  font-family: var(--fr-font-display, inherit);
  font-size: 12px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -4px rgba(0, 0, 0, 0.1);
  animation: gb-rise-in 500ms ease;
  cursor: pointer;
}

/* pointer-events-none absolute inset-0 overflow-hidden */
.gbr-confetti {
  pointer-events: none;
  position: absolute;
  inset: 0;
  overflow: hidden;
}

/* absolute left-1/2 top-1/2 h-2.5 w-1.5 rounded-sm */
.gbr-confetti-piece {
  position: absolute;
  left: 50%;
  top: 50%;
  height: 10px;
  width: 6px;
  border-radius: 2px;
}
</style>
